"use client";

import { useId, forwardRef } from "react";
import styled from "styled-components";
import { spacing4, spacing8, textColorTertiary } from "@churchofjesuschrist/eden-style-constants";
import { InternalUseOnlyInputGroup } from "./InputGroup";
import { InternalUseOnlyFileInput } from "./FileInput";
import { InternalUseOnlyInput } from "./Input";
import { InternalUseOnlyTextArea } from "./TextArea";
import { InternalUseOnlySelect } from "./Select";
import { InternalUseOnlyRangeInput } from "./RangeInput";
import { InternalUseOnlyMultiRangeField } from "./MultiRangeField";
import Hint, { InternalUseOnlyHint } from "./Hint";
import Label, { InternalUseOnlyLabel } from "./Label";
import Tooltip from "./Tooltip";
import RequiredIndicator, { InternalUseOnlyRequiredIndicator } from "./RequiredIndicator";
import Row from "@churchofjesuschrist/eden-row";
import { ValidityStateContext, IdContext, DescribedByContext } from "../utils/context";
import useValidation, { getValidityState } from "../utils/useValidation";
import { ConditionalWrapper, deepMapChildren } from "@churchofjesuschrist/eden-utilities";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Container = styled.div.withConfig({
  componentId: "sc-xne1im-0"
})(["display:", ";flex-wrap:wrap;gap:", " ", ";grid-auto-columns:100%;& ", "{", ";}&:has( :where( ", ",", ",", ",", ",[type=\"file\"],", " ):disabled ) :where(", ",", "){color:", ";cursor:not-allowed;}", " & > ", "{display:block;width:100%;margin-inline-start:", ";}"], ({
  $layout
}) => $layout === "inline" ? "flex" : "grid", spacing4, spacing8, InternalUseOnlyLabel, ({
  layout
}) => `
      display: ${layout === "inline" ? "inline-flex" : "flex"};
      flex-wrap: wrap;
      gap: ${spacing4} ${spacing8};
      align-items: center;
      padding-inline-start: ${layout === "inline" ? null : spacing8};

      & > ${InternalUseOnlyRequiredIndicator} {
        margin-inline-end: -${spacing4};
      }

      & > :where(${InternalUseOnlyInput},
        ${InternalUseOnlyTextArea},
        ${InternalUseOnlySelect},
        ${InternalUseOnlyInputGroup},
        ${InternalUseOnlyFileInput},
        ${InternalUseOnlyRangeInput},
        ${InternalUseOnlyMultiRangeField}) {
        ${layout === "inline" ? "width: auto;" : `flex-basis: calc(100% + ${spacing8});
               margin-inline-start: -${spacing8};

               &[readonly]{
                margin-block-start: -${spacing4};
               }
               `}
      }
    `, InternalUseOnlyInput, InternalUseOnlyTextArea, InternalUseOnlySelect, InternalUseOnlyFileInput, InternalUseOnlyRangeInput, InternalUseOnlyLabel, InternalUseOnlyHint, textColorTertiary, ({
  layout
}) => layout === "inline" ? `& > :not(${InternalUseOnlyHint}) {
          width: auto;
        }` : "", InternalUseOnlyHint, spacing8);

/**
 * The `FormField` is used to group one control, excluding `Radio` and `Checkbox`, with helper components that describe the control.
 * @param {object} props - The component props
 * @param {React.ReactNode} props.children - Content to render inside the FormField Component. Should contain one form control. Not for use with `Radio` and `Checkbox`.
 * @param {React.ReactNode} [props.description] - A brief description of how to fill in the input
 * @param {React.ReactNode} [props.label] - A label for the input. Will automatically add a required indicator if children are determined to be required.
 * @param {"inline" | "block"} [props.layout] - Displays label inline with input
 * @param {Function} [props.onValidate] - Callback for handling custom error and warning messages
 * @param {React.ReactNode} [props.tooltip] - A long description of the input hidden until user interaction
 * @param {React.Ref} ref - Ref forwarded to the underlying element.
 * @returns {React.ReactElement} The FormField component
 */
const FormFieldComponent = ({
  children,
  description,
  layout = "block",
  label,
  onValidate,
  tooltip,
  ...props
}, ref) => {
  const inputId = useId();
  const hintId = useId();
  const tooltipCalloutId = useId();
  let hasHint = false;
  let hasLabel = false;
  let isRequired = false;
  let childId = false;
  deepMapChildren(children, child => {
    if (child?.type === Hint) {
      hasHint = true;
    }
    if (child?.type === Label) {
      hasLabel = true;
    }
    if (child?.props?.required) {
      isRequired = true;
    }
    if (/* Checks custom property on components so it only applies to our input type elements. The target check is for styled-components support. */
    child?.props?.id && (child?.type?.isInput || child?.type?.target?.isInput)) {
      childId = child.props.id;
    }
  });
  if (hasLabel && (tooltip || label)) {
    console.warn("A child Label component is detected. The tooltip and label property cannot be used.");
  }
  if (hasHint && description) {
    console.warn("A child Hint component is detected. The description property cannot be used.");
  }
  const {
    validationMessage,
    warningMessage
  } = useValidation(childId ? childId : inputId, onValidate);
  const showHint = !hasHint && (description || validationMessage || warningMessage);
  const getDescribedBy = () => {
    const ids = [];
    if (tooltip) {
      ids.push(tooltipCalloutId);
    }
    if (showHint) {
      ids.push(hintId);
    }
    return ids.length === 0 ? undefined : ids.join(" ");
  };
  return /*#__PURE__*/_jsx(Container, {
    ref: ref,
    $layout: layout,
    ...props,
    children: /*#__PURE__*/_jsx(ValidityStateContext.Provider, {
      value: getValidityState(validationMessage, warningMessage),
      children: /*#__PURE__*/_jsx(IdContext.Provider, {
        value: childId ? childId : inputId,
        children: /*#__PURE__*/_jsxs(DescribedByContext.Provider, {
          value: getDescribedBy(),
          children: [!hasLabel ? /*#__PURE__*/_jsxs(ConditionalWrapper, {
            condition: label && tooltip,
            wrapper: /*#__PURE__*/_jsx(Row, {
              verticalAlign: "center",
              gapSize: "4"
            }),
            children: [label ? /*#__PURE__*/_jsxs(Label, {
              htmlFor: childId ? childId : inputId,
              children: [isRequired ? /*#__PURE__*/_jsx(RequiredIndicator, {}) : null, label]
            }) : null, tooltip ? /*#__PURE__*/_jsx(Tooltip, {
              calloutId: tooltipCalloutId,
              children: tooltip
            }) : null]
          }) : null, children, showHint ? /*#__PURE__*/_jsxs(Hint, {
            id: hintId,
            children: [validationMessage ? validationMessage : null, !validationMessage && warningMessage ? warningMessage : null, description ? validationMessage || warningMessage ? " " + description : description : null]
          }) : null]
        })
      })
    })
  });
};
const FormField = /*#__PURE__*/forwardRef(FormFieldComponent);
FormField.displayName = "FormField";
export default FormField;